<template>
  <div
    class="container search mobile-content-adjust"
    :class="{ 'search--form-active': showFormOnMobile }"
    data-page-identifier="new-search"
  >
    <div v-if="showFilteredSearchLoading" class="fixed inset-0 bg-white z-50 flex items-center justify-center">
      <FilteredSearchLoading :search-results-loaded="searchResultsLoaded" @loading-complete="onLoadingComplete" />
    </div>
    <div v-if="!showFormOnMobile" class="search__filters mobile-filters-adjust">
      <a href="#" class="search__filter" :class="{ 'search__filter--active': displayRate }" @click.prevent="openForm">{{
        ratePill
      }}</a>
      <a href="#" class="search__filter" :class="{ 'search__filter--active': displayZipcode }" @click.prevent="openForm">{{
        wherePill
      }}</a>
      <a href="#" class="search__filter search__filter--active" @click.prevent="openForm">Child care</a>
      <!-- <a href="#" class="search__filter" :class="{ 'search__filter--active': when }" @click.prevent="openForm">{{ whenPill }}</a> -->
      <a href="#" class="search__filter search__filter--active" @click.prevent="openForm">All filters</a>
    </div>

    <div :class="{ overlay: true, 'lg:mt-32 mt-16': isBasic }">
      <Notification :message="notification" :trigger-notification="showNotification" @toggle-visibility="hideToast" />
      <aside
        ref="overlayAside"
        class="overlay__aside overlay__aside--tablet overlay__aside--scrollable"
        :class="{ 'overlay__aside--active': showFormOnMobile }"
      >
        <SearchForm ref="searchForm" :account-type="accountType" @close="closeForm" />
      </aside>

      <div
        class="overlay__main overlay__main--tablet overlay__main--scrollable"
        :class="{ 'overlay__main--hiding': showFormOnMobile }"
      >
        <div class="content-container">
          <header class="heading-bar-sm">
            <div class="heading-bar-main">
              <h1 class="hdg hdg--2" aria-live="polite" aria-atomic="true">{{ sitterSearchHeading }}</h1>
              <div class="heading-bar-main__sort">
                Sorted by:
                <span class="heading-bar-main__strong">Relevance</span>
                <a
                  href="https://support.sittercity.com/hc/en-us/articles/360022401334-How-do-I-find-the-sitters-that-are-most-relevant-to-me-"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <HelpOutlineIcon :width="16" :height="16" />
                </a>
              </div>
            </div>
          </header>

          <p v-if="showFiltersPreselectedMessage" class="text-body mb-16">Filters are preselected from your job post</p>
          <div
            v-if="hasSitters && !loading"
            class="flex items-center gap-8 bg-grey-background-contrast p-16 mb-16 rounded-xl border-solid border-1 border-grey-divider-subdued"
          >
            <MessageInboxIcon color="#FF0064" :height="20" :width="20" class="shrink-0" />
            <p class="text-body">See a sitter you like? Invite them to apply to your job</p>
          </div>
          <SearchList
            v-if="hasSitters && !loading"
            :search-results="sitters"
            :caretype="'babysitter'"
            :zip="zipcode || ''"
            :availability-checks="availabilityChecks"
            :account="account"
            :show-invite-button="shouldShowInviteButton"
            :parent-jobs="parentJobs"
            :search-care-type="searchCareType"
            :current-page="currentPage"
            @sitter-recruited="handleSitterRecruited"
          />
          <PaginationControls
            v-if="showPaginationControls"
            :total-items="totalSitterCount"
            :items-per-page="itemsPerPage"
            :starting-page="currentPage"
            @page-change="handlePageChange"
          />
          <p v-if="hasFewSitters && !loading" class="text-body text-grey-body text-center mt-16">
            No other sitters fully match your criteria. Try adjusting filters to see more options.
          </p>
          <SearchLoadingSkeleton v-if="loading" />
          <SearchEmptyState v-else-if="noSitters" :zip="zipcode" :has-backup-results="!!backupResults.sitters?.length" />

          <div v-if="showBackupResults" class="mt-80">
            <h2 class="text-h3 mb-16">Here are more sitters near you:</h2>
            <SearchList
              :search-results="backupResults.sitters"
              :caretype="'babysitter'"
              :zip="zipcode || ''"
              :show-b-h-partners="noSitters"
              :availability-checks="availabilityChecks"
              :account="account"
              :show-invite-button="shouldShowInviteButton"
              :parent-jobs="parentJobs"
              :search-care-type="searchCareType"
              @sitter-recruited="handleSitterRecruited"
            />
            <SearchLoadingSkeleton v-if="loadingBackup" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import get from 'lodash/get';
import mapKeys from 'lodash/mapKeys';
import pickBy from 'lodash/pickBy';
import snakeCase from 'lodash/snakeCase';
import { v4 as uuidv4 } from 'uuid';
import { subDays, format, isValid, formatISO } from 'date-fns';

import Notification from '@/components/notification/Notification.vue';
import SearchForm from '@/apps/search/SearchForm.vue';
import SearchList from '@/apps/search/SearchList.vue';
import PaginationControls from '@/components/pagination_controls/PaginationControls.vue';
import {
  SEARCH_AVAILABILITIES_QUERY,
  SITTER_SEARCH_BY_JOB_QUERY,
  FETCH_PARENT_OPEN_JOBS,
  JobEdge,
} from '@/graphql/search/queries';
import { parseTimeParam, validate } from '@/lib/search/form_utils';
import { sendSegmentEvent } from '../../lib/segment';
import { sendBrazeEvent } from '../../lib/braze';
import { SITTER_SEARCH_LOADED } from '@/lib/constants/braze_custom_events';
import { AVAILABILITY_CHECKS } from '@/graphql/availability/queries';
import { isOn } from '@/lib/growthbook';
import SearchResponseHandler from './helpers/searchResponseHandler';
import FilteredSearchLoading from './FilteredSearchLoading.vue';
import SearchLoadingSkeleton from './SearchLoadingSkeleton.vue';
import SearchEmptyState from './SearchEmptyState.vue';
import { allowedJobCareTypesForSearchCareType } from '@/helpers/allowedJobCareTypes';
import { redirectParentRegistration } from '@/helpers/parentRegistrationChecks';
import HelpOutlineIcon from '@/graphics/icons/HelpOutlineIcon.vue';
import MessageInboxIcon from '@/graphics/icons/MessageInboxIcon.vue';
import { geocode } from '@/lib/geocode';

export default {
  name: 'Search',
  components: {
    SearchList,
    SearchForm,
    Notification,
    FilteredSearchLoading,
    SearchLoadingSkeleton,
    SearchEmptyState,
    HelpOutlineIcon,
    MessageInboxIcon,
    PaginationControls,
  },
  data(): any {
    return {
      notification: null,
      loading: true,
      showFormOnMobile: false,
      showNotification: false,
      searchParams: null,
      searchResults: null,
      sitters: null,
      availabilityChecks: null,
      handlePageShow: null,
      parentJobs: [] as JobEdge[],
      searchResultsLoaded: false,
      showFilteredSearchLoading: false,
      isFromJobPost: false,
      isPopulatingFilters: false,
      filtersPopulatedFromJob: false,
      showFiltersPreselectedMessage: false,
      loadingBackup: false,
      jobZipcode: null,
      backupResults: {
        sitters: [],
        pageInfo: null,
      },
      currentPage: 1,
      itemsPerPage: 10,
      currentSearchUsedJobQuery: false,
    };
  },
  computed: {
    showFilteredResultsByJob(): boolean {
      return this.hasMatchingCareTypeJobs;
    },
    selectedJobId(): string | null {
      if (!this.showFilteredResultsByJob) return null;

      const firstMatchingJob = this.parentJobs.find(
        (edge: JobEdge) => edge.node.careType && this.allowedJobCareTypes.includes(edge.node.careType)
      );
      return firstMatchingJob?.node?.id || null;
    },
    useFilterByJobQuery(): boolean {
      return this.showFilteredResultsByJob && !!this.selectedJobId && !this.filtersPopulatedFromJob;
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    totalSitterCount(): number {
      return this.searchResults?.totalCount || 0;
    },
    accountType(): boolean {
      return this.account?.accountType();
    },
    isParent(): boolean {
      return this.account?.isParent() || false;
    },
    searchCareType(): string {
      const rawCareType = this.$route.query?.careType;
      const normalizedCareType = rawCareType ? rawCareType.toLowerCase() : '';
      if (normalizedCareType === 'both' || normalizedCareType === 'childcare') {
        return 'childcare';
      }
      if (normalizedCareType === 'babysitter' || normalizedCareType === 'nanny') {
        return normalizedCareType;
      }
      return 'babysitter';
    },
    allowedJobCareTypes(): string[] {
      return allowedJobCareTypesForSearchCareType(this.searchCareType);
    },
    hasMatchingCareTypeJobs(): boolean {
      return this.parentJobs.some((edge: JobEdge) => edge.node.careType && this.allowedJobCareTypes.includes(edge.node.careType));
    },
    shouldShowInviteButton(): boolean {
      if (!this.isParent) {
        return false;
      }
      if (!this.hasMatchingCareTypeJobs) {
        return false;
      }

      return isOn('sc-invite-to-apply-sitter-search');
    },
    sitterSearchHeading(): string {
      if (!this.searchResults || this.sitters === null) return 'Loading...';

      const formattedCount: number | string = this.totalSitterCount < 1000 ? this.totalSitterCount : '1000+';
      if (this.totalSitterCount === 0) return '0 matching sitters found';
      return `${formattedCount} sitters that might fit your care needs`;
    },
    isBasic(): boolean {
      return this.accountType === 'basic';
    },
    hasSitters(): boolean {
      return this.sitters && this.sitters.length > 0;
    },
    noSitters(): boolean {
      return this.totalSitterCount === 0;
    },
    hasFewSitters(): boolean {
      return this.hasSitters && this.totalSitterCount <= 3;
    },
    showPaginationControls(): boolean {
      return this.hasSitters && !this.loading && this.totalSitterCount > this.itemsPerPage;
    },
    showBackupResults(): boolean {
      if (!this.backupResults.sitters || this.backupResults.sitters.length === 0) {
        return false;
      }
      return this.noSitters || this.hasFewSitters;
    },
    returnToAllSearch(): string {
      const searchRedirect = '/availability/search';
      const params = new URLSearchParams();
      if (this.searchCareType) params.set('careType', this.searchCareType);
      if (this.zipcode) params.set('zipcode', this.zipcode);
      const query = params.toString();
      return query ? `${searchRedirect}?${query}` : searchRedirect;
    },
    zipcode(): string | null {
      if (!this.valid) return null;
      return get(this.queryParams, 'zipcode', null);
    },
    displayZipcode(): string | null {
      if (this.showFilteredResultsByJob && this.jobZipcode) {
        return this.jobZipcode;
      }
      return this.zipcode || this.account?.location?.zipcode || null;
    },
    wherePill(): string {
      return this.displayZipcode || 'Where';
    },
    rate(): number | null {
      if (!this.valid) return null;
      return get(this.queryParams, 'rate', null);
    },
    displayRate(): number {
      return this.rate || 20;
    },
    ratePill(): string {
      return `$${this.displayRate}/hr`;
    },
    when(): Date | null {
      if (!this.valid) return null;
      return get(this.queryParams, 'start', null);
    },
    whenPill(): string {
      if (this.when && isValid(this.when)) return format(this.when, 'MMM d');
      return 'When';
    },
    queryParams(): any {
      if (!this.$route.query) return {};

      const params: any = {
        zipcode: this.$route.query.zipcode,
        sid: this.$route.query.sid || uuidv4(),
        keywords: this.$route.query.keywords,
      };

      const {
        start,
        end,
        distance,
        hasCar,
        isComfortableWithPets,
        hasMultiplesExperience,
        hasSpecialNeedsExperience,
        rate,
        yearsExperience,
        providesSchoolSupport,
        languages,
        lastLoginDays,
        backgroundChecks,
        responsibilities,
        ageRanges,
      } = this.$route.query;

      if (start) params.start = parseTimeParam(start);
      if (end) params.end = parseTimeParam(end);

      const parsedDistance = parseInt(distance, 10);
      if (parsedDistance > 0) params.distance = parsedDistance;

      if (hasCar) params.hasCar = hasCar === 'true';
      if (isComfortableWithPets) params.isComfortableWithPets = isComfortableWithPets === 'true';
      if (hasMultiplesExperience) params.hasMultiplesExperience = hasMultiplesExperience === 'true';
      if (hasSpecialNeedsExperience) params.hasSpecialNeedsExperience = hasSpecialNeedsExperience === 'true';
      if (providesSchoolSupport) params.providesSchoolSupport = providesSchoolSupport === 'true';

      // DB value is stored in the hundredths
      const parsedRate = parseInt(rate, 10);
      // specifically allow sending $0 rate to the search api
      if (parsedRate >= 0) params.rate = parsedRate;

      const parsedYearsExperience = parseInt(yearsExperience, 10);
      if (parsedYearsExperience) params.yearsExperience = parsedYearsExperience;

      if (!!languages && languages.length !== 0) {
        if (Array.isArray(languages)) {
          params.languages = languages;
        } else {
          params.languages = Array.of(languages);
        }
      }

      const d = subDays(new Date(), lastLoginDays || 42);
      params.lastLoginAt = format(d, 'yyyy-MM-dd');

      if (!!backgroundChecks && backgroundChecks.length !== 0) {
        if (Array.isArray(backgroundChecks)) {
          params.backgroundChecks = backgroundChecks;
        } else {
          params.backgroundChecks = Array.of(backgroundChecks);
        }
      }

      if (!!responsibilities && responsibilities.length !== 0) {
        if (Array.isArray(responsibilities)) {
          params.responsibilities = responsibilities;
        } else {
          params.responsibilities = Array.of(responsibilities);
        }
      }

      if (!!ageRanges && ageRanges.length !== 0) {
        if (Array.isArray(ageRanges)) {
          params.personAgeExperiences = ageRanges;
        } else {
          params.personAgeExperiences = Array.of(ageRanges);
        }
      }

      return pickBy(params, (p) => !!p || p === 0);
    },
    valid(): boolean {
      const errors = validate(this.queryParams);
      return Object.keys(errors).length === 0;
    },
  },
  watch: {
    $route: function () {
      if (this.isPopulatingFilters || !this.searchResultsLoaded) {
        return;
      }
      this.showFiltersPreselectedMessage = false;
      this.currentSearchUsedJobQuery = false;
      // Don't reset filtersPopulatedFromJob - once populated, it stays true for the session
      this.submitSearch();
    },
  },
  beforeMount() {
    this.handlePageShow = (event) => {
      if (event.persisted) {
        // safari has a quirk where it doesn't reload the page on back navigation and instead loads cached exit state, this forces reload
        // this was breaking filters when going from childcare to special needs
        window.location.reload();
      }
    };
    window.onpageshow = this.handlePageShow;
  },
  async mounted() {
    this.initFilteredSearchLoading();

    sendBrazeEvent(SITTER_SEARCH_LOADED);
    // turn off buggy chrome scroll scrollRestoration
    // this self restores when you navigate somewhere else
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    await this.fetchParentJobs();
    if (await this.redirectPremiumParentToAddressIfInviteToApply()) {
      return;
    }

    this.submitSearch();
    this.checkRecruits();
  },
  beforeUnmount() {
    // Clean up window event listener
    if (this.handlePageShow) {
      window.onpageshow = null;
    }
  },
  methods: {
    async submitSearch(): Promise<any> {
      if (!this.valid) return null;

      sendSegmentEvent('Sitter Search Requested', this.sitterSearchRequestedParams(this.queryParams));
      this.showFormOnMobile = false;

      if (this.zipcode) {
        try {
          const geocoded = await geocode(this.queryParams.zipcode);
          if (!geocoded?.location) {
            console.error('Geocode was not successful', this.queryParams.zipcode);
            this.notify('Please enter a valid zipcode.');
            this.loading = false;
            return null;
          }
          this.runSearch(this.buildSearchParams(geocoded.location.x, geocoded.location.y));
        } catch (error) {
          console.error(error);
          this.notify('Please enter a valid zipcode.');
          this.loading = false;
        }
        return null;
      }

      return this.submitDefaultSearch();
    },
    submitDefaultSearch() {
      if (!this.valid) return null;

      if (!this.account || !this.account.location) {
        console.error('Account or location data not available for default search');
        return null;
      }

      return this.runSearch(this.buildSearchParams(this.account.location.longitude, this.account.location.latitude));
    },
    sitterSearchRequestedParams(segParams: any): any {
      const { sid, zipcode, start, end, rate, ...rest } = segParams;
      const params: any = {
        ...rest,
        search_uuid: sid,
      };

      if (zipcode) params.postal_code = zipcode;
      if (start) params.start_time = formatISO(start);
      if (end) params.end_time = formatISO(end);

      if (rate >= 0) params.rate = rate * 100;

      return mapKeys(params, (value, key) => snakeCase(key));
    },
    // TODO: when GraphQL work is completed, we should change locationX and locationY to numbers)
    buildSearchParams(locationX: any, locationY: any): any {
      const { sid: _sid, zipcode: _zipcode, start, end, rate, keywords, ...rest } = this.queryParams;

      const params: any = {
        ...rest,
        geoPoint: {
          x: locationX.toString(),
          y: locationY.toString(),
        },
      };

      if (start) params.start = formatISO(start);
      if (end) params.end = formatISO(end);

      const defaultHourlyRate = 20;
      if (rate >= 0) {
        params.rate = rate * 100;
      } else {
        params.rate = defaultHourlyRate * 100;
      }

      if (keywords) params.keywords = keywords;

      return params;
    },
    runSearch(params: any) {
      // clear previous results
      this.searchResults = null;
      this.sitters = null;
      this.backupResults = {
        sitters: [],
        pageInfo: null,
      };
      this.loadingBackup = false;
      this.jobZipcode = null;
      this.currentPage = 1;

      // set up for new results
      this.loading = true;
      this.searchParams = params;
      this.scrollToTop();

      const query = this.useFilterByJobQuery ? SITTER_SEARCH_BY_JOB_QUERY : SEARCH_AVAILABILITIES_QUERY;
      const variables = this.useFilterByJobQuery
        ? { jobId: this.selectedJobId, first: this.itemsPerPage, backupFirst: 10 }
        : { first: this.itemsPerPage, searchParams: params };

      // Track which query type is being used for this search session
      this.currentSearchUsedJobQuery = this.useFilterByJobQuery;

      this.$apollo
        .query({
          query,
          errorPolicy: 'all',
          variables,
        })
        .then((payload) => {
          SearchResponseHandler.updateSearchResults(this, payload, false);
          this.searchResultsLoaded = true;

          if (this.useFilterByJobQuery) {
            this.populateFiltersFromJob(payload);
            this.filtersPopulatedFromJob = true;
            this.showFiltersPreselectedMessage = true;

            const backupExecuted = get(payload, 'data.sitterSearchByJob.backup.executed');
            if (backupExecuted) {
              SearchResponseHandler.updateBackupResults(this, payload);
            }
          }
        })
        .catch(this.onError);
    },
    populateFiltersFromJob(payload: any) {
      const filters = get(payload, 'data.sitterSearchByJob.filters');
      if (!filters) return;

      // Store the job zipcode from filters
      this.jobZipcode = filters.zipcode || null;

      this.isPopulatingFilters = true;
      // Ensure SearchForm ref is available before calling method
      this.$nextTick(() => {
        if (this.$refs.searchForm) {
          this.$refs.searchForm.populateFiltersFromJob(filters);
        }
        // Use setTimeout to ensure route watcher is skipped during filter population
        setTimeout(() => {
          this.isPopulatingFilters = false;
        }, 100);
      });
    },
    checkRecruits() {
      this.$apollo
        .query({
          query: AVAILABILITY_CHECKS,
        })
        .then(({ data }) => {
          this.availabilityChecks = data?.availabilityChecks || [];
        })
        .catch((_error) => {
          this.availabilityChecks = [];
        });
    },
    handleSitterRecruited(sitterUuid: string | null) {
      if (!sitterUuid) {
        return;
      }
      // Add the sitter to the local availabilityChecks array to update the UI immediately
      const checks = this.availabilityChecks || [];
      if (!checks.includes(sitterUuid)) {
        this.availabilityChecks = [...checks, sitterUuid];
      }
    },
    async fetchParentJobs(): Promise<void> {
      await this.$apollo
        .query({
          query: FETCH_PARENT_OPEN_JOBS,
          variables: {
            careTypes: this.allowedJobCareTypes,
          },
        })
        .then(({ data }) => {
          this.parentJobs = data?.viewer?.jobs?.edges || [];
        })
        .catch(() => {
          this.parentJobs = [];
        });
    },
    async redirectPremiumParentToAddressIfInviteToApply(): Promise<boolean> {
      if (!this.account?.isPremium?.() || !this.shouldShowInviteButton) {
        return false;
      }

      try {
        return await redirectParentRegistration(this.$apollo, this.$route.fullPath);
      } catch (error) {
        console.error('Error checking premium parent address onboarding for search:', error);
        return false;
      }
    },
    searchResultReceived(payload) {
      const e = get(payload, 'data.sitterSearch.edges');
      if (e && e.length > 0) {
        e.forEach((edge) => {
          sendSegmentEvent('Sitter Search Returned', {
            search_uuid: this.queryParams.sid,
            sitter_uuid: get(edge, 'node.sitter.id'),
            has_photo: !!get(edge, 'node.sitter.photoUrl'),
            average_rating: get(edge, 'node.avgRating'),
            distance: get(edge, 'node.distance'),
          });
        });
      }
    },
    onError(error) {
      console.error(error);
      this.notify("Oops! We can't process this search right now. Try clicking the 'All Sitters' tab to browse local sitters.");
      this.loading = false;
    },
    encodeCursor(offset: number): string {
      return btoa(offset.toString());
    },
    handlePageChange(page: number) {
      if (page === this.currentPage) return;

      this.currentPage = page;
      this.loading = true;
      this.scrollToTop();

      const offset = (page - 1) * this.itemsPerPage;
      const cursor = offset > 0 ? this.encodeCursor(offset) : null;
      // Use the same query type that was used for the current search results
      const query = this.currentSearchUsedJobQuery ? SITTER_SEARCH_BY_JOB_QUERY : SEARCH_AVAILABILITIES_QUERY;
      const variables = this.currentSearchUsedJobQuery
        ? {
            jobId: this.selectedJobId,
            first: this.itemsPerPage,
            after: cursor,
          }
        : {
            first: this.itemsPerPage,
            after: cursor,
            searchParams: this.searchParams,
          };

      this.$apollo
        .query({
          query,
          errorPolicy: 'all',
          variables,
        })
        .then((payload) => {
          SearchResponseHandler.updateSearchResults(this, payload, false);
        })
        .catch(this.onError);
    },
    notify(message: string) {
      this.notification = message;
      this.showNotification = true;
    },
    hideToast() {
      this.showNotification = false;
    },
    openForm() {
      this.showFormOnMobile = true;
      this.scrollToTop();
    },
    closeForm() {
      this.showFormOnMobile = false;
    },
    scrollToTop() {
      window.scrollTo(0, 0);
      const container = document.querySelector('.overlay__main--scrollable');
      if (container) {
        container.scrollTo(0, 0);
      }
    },
    initFilteredSearchLoading() {
      this.isFromJobPost = this.$route.query.source === 'job-post';
      this.showFilteredSearchLoading = this.isFromJobPost;
    },
    onLoadingComplete() {
      this.showFilteredSearchLoading = false;

      // Remove source query param to prevent showing loading again on refresh
      // This is done after loading completes to avoid triggering guards during the loading animation
      if (this.isFromJobPost) {
        const { source: _source, ...query } = this.$route.query;
        this.$router.replace({ query });
      }
    },
  },
};
</script>

<style scoped>
.overlay__aside--scrollable {
  overflow-y: auto;
  max-height: calc(100vh - 80px); /* Adjust based on header height */
  position: sticky;
  top: 80px;
}

.overlay__main--scrollable {
  overflow-y: auto;
  max-height: calc(100vh - 80px); /* Adjust based on header height */
}

@media (max-width: 1023px) {
  .overlay__aside--scrollable,
  .overlay__main--scrollable {
    overflow-y: visible;
    max-height: none;
    position: static;
  }
}
</style>
